/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-indent */
import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useQuery} from 'react-query';
import {connect} from 'react-redux';
import moment from 'moment';
import {toast} from 'react-toastify';
import * as Actions from '../../../store/actions';
import {
  DashboardLayout,
  EmptyState,
  PageLimit,
  Pagination,
  SearchInput,
  TableLayout,
  Loader,
  SecondaryButton,
} from '../../../components';
import {corruptedFile, eye, lookupEmpty} from '../../../assets/images/images';
import {filterOutEmptyValues} from '../../../helpers';
import {fetchReport} from '../../../requests/queries/report';
import {PageContext} from '../../../helpers/context';
import {useAllApps} from '../../../hooks/useApps';
import {useNavigate} from 'react-router-dom';
import LogDetailsModal from './LogDetailsModal';
import FilterV2 from '../../../components/FilterV2';
import CardSummary from 'components/CardSummary';
import PageNumber from 'components/PageNumber';

const messageTypes = [
  {value: 'sms', label: 'SMS', name: 'msg_type'},
  {value: 'whatsapp', label: 'WhatsApp', name: 'msg_type'},
  {value: 'sms_otp', label: 'SMS OTP', name: 'msg_type'},
];

function MessagingReport() {
  const navigate = useNavigate();
  const {setPageName} = useContext(PageContext);
  const {userApps} = useAllApps();

  const [openFilter, setOpenFilter] = useState(false);
  const [filterValues, setFilterValues] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setPageName('messagingreport');
  }, [setPageName]);

  const appOptions = useMemo(
    () =>
      userApps?.map(app => ({
        value: app.value,
        label: app.label,
      })) || [],
    [userApps],
  );

  const allFilters = useMemo(
    () => [
      ...(appOptions.length > 0
        ? [
            {
              key: 'apps',
              label: 'Apps',
              type: 'select',
              options: appOptions,
              multiSelect: true,
            },
          ]
        : []),
      {
        key: 'msg_type',
        label: 'Message Type',
        type: 'select',
        options: messageTypes,
        multiSelect: true,
      },
    ],
    [appOptions],
  );

  const allApps = userApps?.map(app => app.value)?.join(',');
  const filteredApps = sessionStorage.getItem('filtered_apps');

  const {data: messagingReport, isLoading} = useQuery(
    [
      'messaging-report',
      {page: currentPage, limit, allApps, filteredApps, ...filterValues},
    ],
    () =>
      fetchReport(filteredApps ?? allApps, {
        page: currentPage,
        limit,
        ...filterValues,
      }),
    {
      onSuccess: data => {
        toast.success(data.message);
        setFilterValues({
          ...filterValues,
          download: undefined,
        });
      },
      onError: error => {
        toast.error(error.response.data.error);
      },
      enabled: !!allApps,
      retry: 1,
    },
  );

  const pageClick = selected => {
    setCurrentPage(selected);
    const searchParams = new URLSearchParams(document.location.search);
    searchParams.set('page', selected);
    navigate(`?${searchParams.toString()}`);
  };

  const messagingReportData = useMemo(
    () => (!isLoading && messagingReport?.rows) || [],
    [isLoading, messagingReport?.rows],
  );

  const messagingReportOverview = messagingReport?.overview || [];
  const totalMessagesSent =
    messagingReportOverview.find(item => item.status === 'sent')?.count || 0;
  const totalMessagesFailed =
    messagingReportOverview.find(item => item.status === 'failed')?.count || 0;
  const totalMessages = totalMessagesSent + totalMessagesFailed;

  const handleFilterChange = newFilters => {
    const filteredOptions = filterOutEmptyValues(newFilters);

    if (filteredOptions.dateRange) {
      filteredOptions.date_from = filteredOptions.dateRange.start;
      filteredOptions.date_to = filteredOptions.dateRange.end;
      delete filteredOptions.dateRange;
    }

    if (filteredOptions.apps) {
      const appIds = Array.isArray(filteredOptions.apps)
        ? filteredOptions.apps.join(',')
        : filteredOptions.apps;
      sessionStorage.setItem('filtered_apps', appIds);
    } else {
      sessionStorage.removeItem('filtered_apps');
    }

    if (filteredOptions.msg_type) {
      filteredOptions.msg_type = Array.isArray(filteredOptions.msg_type)
        ? filteredOptions.msg_type.join(',')
        : filteredOptions.msg_type;
    }

    setFilterValues(filteredOptions);
    setCurrentPage(1);
    const searchParams = new URLSearchParams();
    if (filteredOptions.start) searchParams.set('start', filteredOptions.start);
    if (filteredOptions.end) searchParams.set('end', filteredOptions.end);
    if (filteredOptions.msg_type)
      searchParams.set('msg_type', filteredOptions.msg_type);
    navigate(`?${searchParams.toString()}`);
  };

  const toggleFilterVisibility = () => {
    setOpenFilter(prev => !prev);
  };

  const isFiltered =
    messagingReportData?.length === 0 &&
    !isLoading &&
    Object.keys(filterValues).length > 0;

  return (
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">Messaging Logs</p>
      }
    >
      <div className="flex items-center gap-2 flex-wrap py-2  mx-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <SearchInput
          onChange={value => {
            const searchParams = new URLSearchParams(document.location.search);
            if (value === '') {
              searchParams.delete('search');
            } else {
              searchParams.set('search', value);
            }
            navigate(`?${searchParams.toString()}`);
            setFilterValues({...filterValues, search: value});
          }}
        />
        <SecondaryButton buttonText="Filter" onClick={toggleFilterVisibility} />
      </div>

      <div className={`filter-slide-container ${openFilter ? 'open' : ''}`}>
        <FilterV2 filters={allFilters} onFilterChange={handleFilterChange} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 items-center gap-4 py-6">
        <CardSummary
          title="Total Messages"
          body={totalMessages}
          loading={isLoading}
        />
        <CardSummary
          title="Messages Sent"
          body={totalMessagesSent}
          loading={isLoading}
        />
        <CardSummary
          title="Messages Not Sent"
          body={totalMessagesFailed}
          loading={isLoading}
        />
      </div>

      <TableLayout negativeMargins>
        <thead className="font-semibold uppercase">
          <tr className="bg-white80">
            <th className="pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
              <span className="flex items-center">date created</span>
            </th>
            <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
              <span className="flex items-center">sender</span>
            </th>
            <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
              <span className="flex items-center">recipient</span>
            </th>
            <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
              <span className="flex items-center">message-type</span>
            </th>
            <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
              <span className="flex items-center">message id</span>
            </th>
            <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
              <span className="flex items-center">status</span>
            </th>
            <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap"></th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td colSpan={8}>
              {isLoading ? (
                <Loader height={45} />
              ) : !isLoading &&
                Object.keys(filterValues).length === 0 &&
                messagingReportData?.length === 0 ? (
                <EmptyState
                  height={45}
                  body="Gain insight into your messaging activities."
                  src={lookupEmpty}
                  bodyMaxWidth="max-w-[203px]"
                  noBtn
                />
              ) : isFiltered ? (
                <EmptyState
                  height={45}
                  body="This filter did not return any data, try using different values."
                  src={corruptedFile}
                  noBtn
                  customBtn={
                    <button
                      onClick={() => {
                        setFilterValues({});
                        setCurrentPage(1);
                        sessionStorage.removeItem('filtered_apps');
                        navigate('');
                      }}
                      className="text-brandBlue p-4 text-sm font-medium"
                    >
                      Update preferences
                    </button>
                  }
                />
              ) : null}
            </td>
          </tr>
        </tbody>

        <tbody className="text-sm bg-white">
          {!isLoading &&
            messagingReportData?.map((msg, index) => (
              <tr key={index} className="border-[0.5px] border-[#E4E3EC]">
                <td className="p-4 pl-4 sm:pl-6 xl:pl-8 text-sm text-body whitespace-nowrap">
                  {moment(msg.created).format('MMMM D, YYYY, h:mm a')}
                </td>
                <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                  {msg.sender}
                </td>
                <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                  {msg.recipient}
                </td>
                <td className="p-4 pl-0 text-sm text-body whitespace-nowrap uppercase">
                  {msg.msg_type}
                </td>
                <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                  {msg.message_id || '-'}
                </td>
                <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                  <span
                    className={`uppercase w-fit rounded-full px-2 py-1 mt-1 h-5 flex items-center justify-center ${
                      msg.status === 'sent'
                        ? 'bg-[#F0F6EF] text-[#396C31]'
                        : 'bg-[#FCDFDF] text-[#F15D5F]'
                    } text-xs`}
                  >
                    {msg.status === 'sent' ? 'Sent' : 'Error'}
                  </span>
                </td>
                <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                  <button
                    className="h-4 aspect-square"
                    onClick={() => {
                      setSelectedMessageId(msg.message_id);
                      setIsModalOpen(true);
                    }}
                  >
                    <img src={eye} alt="" />
                  </button>
                </td>
              </tr>
            ))}
        </tbody>
      </TableLayout>

      <LogDetailsModal
        open={isModalOpen}
        setOpen={setIsModalOpen}
        message_id={selectedMessageId}
      />

      {messagingReportData && messagingReportData.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
          <PageNumber
            currentPage={+currentPage}
            limit={limit}
            totalCount={messagingReport?.count || 0}
          />
          <div className="flex items-center gap-5">
            <PageLimit
              onLimitChange={newLimit => {
                setLimit(newLimit);
                setCurrentPage(1);
              }}
              total={messagingReport?.count}
              length={messagingReportData?.length}
              limit={limit}
            />
            <Pagination
              total={Math.ceil((messagingReport?.count || 0) / limit)}
              current={+currentPage}
              onPageChange={pageClick}
            />
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default connect(state => state, Actions)(MessagingReport);
